// src/components/faqs/FAQDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchFaq,
  deleteFaq,
  updateFaqStatus,
  fetchFaqFeedback,
  fetchRelatedFaqs,
  addRelatedFaq,
  removeRelatedFaq,
  deleteFaqImage,
  clearFaqError,
} from '../../store/actions/faqActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';
import Pagination from '../common/Pagination';

const FAQDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    selectedFaq: faq,
    loading,
    error,
    feedback,
    feedbackMeta,
    relatedFaqs,
  } = useSelector((state) => state.faqs);
  const { faqs } = useSelector((state) => state.faqs);

  const [activeTab, setActiveTab] = useState('info');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [showAddRelatedModal, setShowAddRelatedModal] = useState(false);
  const [selectedRelatedFaq, setSelectedRelatedFaq] = useState(null);
  const [relationStrength, setRelationStrength] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageDeleteConfirm, setShowImageDeleteConfirm] = useState(false);

  const statusOptions = [
    { value: 'DRAFT', label: 'Brouillon', color: '#6b7280', bg: '#f3f4f6' },
    { value: 'PENDING_REVIEW', label: 'En attente de relecture', color: '#f59e0b', bg: '#fef3c7' },
    { value: 'PUBLISHED', label: 'Publié', color: '#10b981', bg: '#d1fae5' },
    { value: 'SCHEDULED', label: 'Programmé', color: '#3b82f6', bg: '#dbeafe' },
    { value: 'ARCHIVED', label: 'Archivé', color: '#6b7280', bg: '#e5e7eb' },
  ];

  const ratingLabels = {
    VERY_POOR: 'Très mauvais',
    POOR: 'Mauvais',
    NEUTRAL: 'Neutre',
    GOOD: 'Bon',
    EXCELLENT: 'Excellent',
  };

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
      textDecoration: 'none',
    },
    editButton: {
      backgroundColor: '#4f46e5',
      color: '#ffffff',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    feedbackButton: {
      backgroundColor: '#0891b2',
      color: '#ffffff',
    },
    feedbackButtonHover: {
      backgroundColor: '#0e7490',
    },
    relatedButton: {
      backgroundColor: '#8b5cf6',
      color: '#ffffff',
    },
    relatedButtonHover: {
      backgroundColor: '#7c3aed',
    },
    statusButton: {
      backgroundColor: '#f59e0b',
      color: '#ffffff',
    },
    statusButtonHover: {
      backgroundColor: '#d97706',
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    hero: {
      padding: '2rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    heroContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    heroLeft: {
      flex: 1,
    },
    faqQuestion: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '1rem',
    },
    faqMeta: {
      display: 'flex',
      gap: '2rem',
      flexWrap: 'wrap',
      marginTop: '1rem',
    },
    metaItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    metaLabel: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      color: '#6b7280',
      marginBottom: '0.25rem',
    },
    metaValue: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#111827',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.5rem 1rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    priorityBadge: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1.5rem',
      marginTop: '2rem',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
    },
    helpful: {
      color: '#10b981',
    },
    notHelpful: {
      color: '#dc2626',
    },
    tabContent: {
      padding: '2rem',
    },
    faqAnswer: {
      fontSize: '1rem',
      lineHeight: '1.8',
      color: '#374151',
    },
    imagesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '1rem',
      marginTop: '1rem',
    },
    imageCard: {
      position: 'relative',
      borderRadius: '0.5rem',
      overflow: 'hidden',
      border: '1px solid #e5e7eb',
      cursor: 'pointer',
      transition: 'transform 0.2s',
    },
    imageCardHover: {
      transform: 'scale(1.02)',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    },
    image: {
      width: '100%',
      height: '150px',
      objectFit: 'cover',
    },
    imageDeleteBtn: {
      position: 'absolute',
      top: '0.5rem',
      right: '0.5rem',
      padding: '0.25rem',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#dc2626',
      opacity: 0,
      transition: 'opacity 0.2s',
    },
    imageCardHoverShow: {
      opacity: 1,
    },
    feedbackList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    feedbackCard: {
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    feedbackHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
    },
    feedbackRating: {
      display: 'inline-flex',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    ratingVeryPoor: { backgroundColor: '#fee2e2', color: '#dc2626' },
    ratingPoor: { backgroundColor: '#fed7aa', color: '#c2410c' },
    ratingNeutral: { backgroundColor: '#fef3c7', color: '#d97706' },
    ratingGood: { backgroundColor: '#d1fae5', color: '#059669' },
    ratingExcellent: { backgroundColor: '#dbeafe', color: '#2563eb' },
    feedbackComment: {
      fontSize: '0.875rem',
      color: '#374151',
      marginBottom: '0.5rem',
      fontStyle: 'italic',
    },
    feedbackMeta: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    relatedList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '1rem',
    },
    relatedCard: {
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    relatedInfo: {
      flex: 1,
    },
    relatedQuestion: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    relatedStrength: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    relatedActions: {
      display: 'flex',
      gap: '0.5rem',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '2rem',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto',
    },
    modalTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      marginBottom: '1rem',
    },
    rangeInput: {
      width: '100%',
      marginBottom: '1rem',
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '1.5rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  const getStatusStyle = (status) => {
    const option = statusOptions.find(s => s.value === status);
    return {
      backgroundColor: option?.bg || '#f3f4f6',
      color: option?.color || '#374151',
    };
  };

  const getRatingStyle = (rating) => {
    switch (rating) {
      case 'VERY_POOR': return styles.ratingVeryPoor;
      case 'POOR': return styles.ratingPoor;
      case 'NEUTRAL': return styles.ratingNeutral;
      case 'GOOD': return styles.ratingGood;
      case 'EXCELLENT': return styles.ratingExcellent;
      default: return {};
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchFaq(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && activeTab === 'feedback') {
      dispatch(fetchFaqFeedback(id, { page: feedbackPage, limit: 10 }));
    }
  }, [dispatch, id, activeTab, feedbackPage]);

  useEffect(() => {
    if (id && activeTab === 'related') {
      dispatch(fetchRelatedFaqs(id));
    }
  }, [dispatch, id, activeTab]);

  useEffect(() => {
    if (faq) {
      setNewStatus(faq.status);
    }
  }, [faq]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteFaq(id));
      navigate('/admin/faqs');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const handleStatusUpdate = useCallback(async () => {
    try {
      await dispatch(updateFaqStatus(id, newStatus));
      setShowStatusModal(false);
    } catch (error) {
      console.error('Status update failed:', error);
    }
  }, [dispatch, id, newStatus]);

  const handleAddRelated = useCallback(async () => {
    if (selectedRelatedFaq) {
      try {
        await dispatch(addRelatedFaq(id, selectedRelatedFaq, relationStrength));
        setShowAddRelatedModal(false);
        setSelectedRelatedFaq(null);
        setRelationStrength(1);
        dispatch(fetchRelatedFaqs(id));
      } catch (error) {
        console.error('Add related failed:', error);
      }
    }
  }, [dispatch, id, selectedRelatedFaq, relationStrength]);

  const handleRemoveRelated = useCallback(async (relatedId) => {
    try {
      await dispatch(removeRelatedFaq(id, relatedId));
      dispatch(fetchRelatedFaqs(id));
    } catch (error) {
      console.error('Remove related failed:', error);
    }
  }, [dispatch, id]);

  const handleImageDelete = useCallback(async () => {
    if (selectedImage) {
      try {
        await dispatch(deleteFaqImage(id, selectedImage.id));
        setShowImageDeleteConfirm(false);
        setSelectedImage(null);
      } catch (error) {
        console.error('Image delete failed:', error);
      }
    }
  }, [dispatch, id, selectedImage]);

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredFaqs = faqs?.filter(f => f.id !== id) || [];

  const tabs = [
    { id: 'info', label: 'Informations' },
    { id: 'answer', label: 'Réponse' },
    { id: 'images', label: `Images (${faq?.images?.length || 0})` },
    { id: 'feedback', label: `Retours (${faq?.helpfulCount + faq?.notHelpfulCount || 0})` },
    { id: 'related', label: `FAQs liées (${relatedFaqs?.length || 0})` },
  ];

  if (loading && !faq) {
    return <LoadingSpinner fullScreen />;
  }

  if (!faq && !loading) {
    return (
      <div style={styles.container}>
        <Alert
          type="error"
          message="FAQ non trouvée"
        />
        <Link to="/admin/faqs" style={styles.backButton}>
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/faqs')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails de la FAQ</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/faqs/${id}/feedback`}
            style={{
              ...styles.actionButton,
              ...styles.feedbackButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.feedbackButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.feedbackButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            Voir tous les retours ({faq?.helpfulCount + faq?.notHelpfulCount || 0})
          </Link>
          <Link
            to={`/admin/faqs/${id}/edit`}
            style={{
              ...styles.actionButton,
              ...styles.editButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>
          <Link
            to={`/admin/faqs/${id}/feedback`}
            style={{
              ...styles.actionButton,
              ...styles.feedbackButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.feedbackButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.feedbackButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            Retours
          </Link>
          <button
            onClick={() => setShowAddRelatedModal(true)}
            style={{
              ...styles.actionButton,
              ...styles.relatedButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.relatedButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.relatedButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Lier une FAQ
          </button>
          <button
            onClick={() => setShowStatusModal(true)}
            style={{
              ...styles.actionButton,
              ...styles.statusButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.statusButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.statusButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Statut
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              ...styles.actionButton,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearFaqError())}
        />
      )}

      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            <div style={styles.heroLeft}>
              <h2 style={styles.faqQuestion}>{faq.question}</h2>
              <div style={styles.faqMeta}>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Statut</span>
                  <span
                    style={{
                      ...styles.badge,
                      ...getStatusStyle(faq.status),
                    }}
                  >
                    {statusOptions.find(s => s.value === faq.status)?.label || faq.status}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Priorité</span>
                  <span style={{ ...styles.badge, ...styles.priorityBadge }}>
                    P{faq.priority || 0}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Catégorie</span>
                  <span style={styles.metaValue}>
                    {faq.category?.name || 'Non catégorisé'}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Auteur</span>
                  <span style={styles.metaValue}>
                    {faq.author ? `${faq.author.firstName} ${faq.author.lastName}` : '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Vues</div>
              <div style={styles.statValue}>{faq.views || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Utile</div>
              <div style={{ ...styles.statValue, ...styles.helpful }}>{faq.helpfulCount || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Pas utile</div>
              <div style={{ ...styles.statValue, ...styles.notHelpful }}>{faq.notHelpfulCount || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Taux d'utilité</div>
              <div style={styles.statValue}>
                {faq.helpfulCount + faq.notHelpfulCount > 0
                  ? Math.round((faq.helpfulCount / (faq.helpfulCount + faq.notHelpfulCount)) * 100)
                  : 0}%
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'info' && (
            <div>
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                  Métadonnées
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Slug
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#111827' }}>
                      /faq/{faq.slug}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Version
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#111827' }}>
                      {faq.version || 1}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Créé le
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#111827' }}>
                      {formatDate(faq.createdAt)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Dernière révision
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#111827' }}>
                      {formatDate(faq.lastReviewedAt) || '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Publié le
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#111827' }}>
                      {formatDate(faq.publishedAt) || '—'}
                    </div>
                  </div>
                </div>
              </div>

              {faq.tags && faq.tags.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                    Tags
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {faq.tags.map(tag => (
                      <span
                        key={tag.id}
                        style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                        }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'answer' && (
            <div
              style={styles.faqAnswer}
              dangerouslySetInnerHTML={{ __html: faq.answer }}
            />
          )}

          {activeTab === 'images' && (
            <div>
              {faq.images && faq.images.length > 0 ? (
                <div style={styles.imagesGrid}>
                  {faq.images.map((image) => (
                    <div
                      key={image.id}
                      style={styles.imageCard}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = styles.imageCardHover.transform;
                        e.currentTarget.style.boxShadow = styles.imageCardHover.boxShadow;
                        const btn = e.currentTarget.querySelector('.delete-btn');
                        if (btn) btn.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                        const btn = e.currentTarget.querySelector('.delete-btn');
                        if (btn) btn.style.opacity = '0';
                      }}
                    >
                      <img
                        src={image.storagePath}
                        alt={image.altText || faq.question}
                        style={styles.image}
                        onClick={() => window.open(image.storagePath, '_blank')}
                      />
                      <button
                        className="delete-btn"
                        style={styles.imageDeleteBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(image);
                          setShowImageDeleteConfirm(true);
                        }}
                      >
                        <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune image associée à cette FAQ
                </div>
              )}
            </div>
          )}

          {activeTab === 'feedback' && (
            <div>
              {feedback && feedback.length > 0 ? (
                <>
                  <div style={styles.feedbackList}>
                    {feedback.map((item) => (
                      <div key={item.id} style={styles.feedbackCard}>
                        <div style={styles.feedbackHeader}>
                          <span
                            style={{
                              ...styles.feedbackRating,
                              ...getRatingStyle(item.rating),
                            }}
                          >
                            {ratingLabels[item.rating]}
                          </span>
                          <span style={styles.feedbackMeta}>
                            {formatDate(item.createdAt)}
                          </span>
                        </div>
                        {item.comment && (
                          <div style={styles.feedbackComment}>
                            "{item.comment}"
                          </div>
                        )}
                        <div style={styles.feedbackMeta}>
                          {item.helpful !== undefined && (
                            <span style={{ marginRight: '1rem' }}>
                              Utile: {item.helpful ? 'Oui' : 'Non'}
                            </span>
                          )}
                          {item.user && (
                            <span>Utilisateur: {item.user.firstName} {item.user.lastName}</span>
                          )}
                          {item.userAgent && (
                            <span style={{ display: 'block', marginTop: '0.25rem' }}>
                              {item.userAgent}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {feedbackMeta && feedbackMeta.totalPages > 1 && (
                    <Pagination
                      currentPage={feedbackPage}
                      totalPages={feedbackMeta.totalPages}
                      onPageChange={setFeedbackPage}
                    />
                  )}
                </>
              ) : (
                <div style={styles.emptyState}>
                  Aucun retour pour cette FAQ
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && (
            <div>
              {relatedFaqs && relatedFaqs.length > 0 ? (
                <div style={styles.relatedList}>
                  {relatedFaqs.map((related) => (
                    <div key={related.id} style={styles.relatedCard}>
                      <div style={styles.relatedInfo}>
                        <Link to={`/admin/faqs/${related.id}/details`} style={styles.relatedQuestion}>
                          {related.question}
                        </Link>
                        <div style={styles.relatedStrength}>
                          Force de relation: {related.relationStrength || 1}/5
                        </div>
                      </div>
                      <div style={styles.relatedActions}>
                        <button
                          onClick={() => handleRemoveRelated(related.id)}
                          style={{
                            padding: '0.25rem',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#dc2626',
                          }}
                        >
                          <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune FAQ liée
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div style={styles.modal} onClick={() => setShowStatusModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              Changer le statut de la FAQ
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
              {statusOptions.filter(s => s.value).map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    backgroundColor: newStatus === option.value ? '#eff6ff' : 'transparent',
                    borderColor: newStatus === option.value ? '#2563eb' : '#e5e7eb',
                  }}
                >
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={newStatus === option.value}
                    onChange={(e) => setNewStatus(e.target.value)}
                    style={{ width: '1rem', height: '1rem' }}
                  />
                  <span style={{ flex: 1 }}>{option.label}</span>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: option.bg,
                      color: option.color,
                      fontSize: '0.75rem',
                    }}
                  >
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowStatusModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={newStatus === faq.status}
                style={{
                  ...styles.saveButton,
                  opacity: newStatus === faq.status ? 0.5 : 1,
                  cursor: newStatus === faq.status ? 'not-allowed' : 'pointer',
                }}
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Related FAQ Modal */}
      {showAddRelatedModal && (
        <div style={styles.modal} onClick={() => setShowAddRelatedModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              Lier une FAQ
            </h3>

            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Sélectionner une FAQ
            </label>
            <select
              style={styles.select}
              value={selectedRelatedFaq || ''}
              onChange={(e) => setSelectedRelatedFaq(e.target.value)}
            >
              <option value="">Choisir une FAQ</option>
              {filteredFaqs.map(f => (
                <option key={f.id} value={f.id}>
                  {f.question}
                </option>
              ))}
            </select>

            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
              Force de la relation (1-5)
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={relationStrength}
              onChange={(e) => setRelationStrength(parseInt(e.target.value))}
              style={styles.rangeInput}
            />
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <span style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                backgroundColor: '#f3f4f6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
              }}>
                {relationStrength}/5
              </span>
            </div>

            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowAddRelatedModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleAddRelated}
                disabled={!selectedRelatedFaq}
                style={{
                  ...styles.saveButton,
                  opacity: !selectedRelatedFaq ? 0.5 : 1,
                  cursor: !selectedRelatedFaq ? 'not-allowed' : 'pointer',
                }}
              >
                Lier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Image Confirmation */}
      <ConfirmDialog
        isOpen={showImageDeleteConfirm}
        onClose={() => {
          setShowImageDeleteConfirm(false);
          setSelectedImage(null);
        }}
        onConfirm={handleImageDelete}
        title="Supprimer l'image"
        message="Êtes-vous sûr de vouloir supprimer cette image ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Delete FAQ Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer la FAQ"
        message="Êtes-vous sûr de vouloir supprimer cette FAQ ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default FAQDetails;